import React, { useState, useMemo } from "react";
import { observer } from "mobx-react";
import userDetail from "Stores/userDetail";
import connectedInboxStore from "./store";
import InboxImage from "./InboxImage";
import QuilEditor from "Components/common/quillEditor";

const MIXED = "__MIXED__";

const resolveInitial = inboxes => {
  const topFields = ["fromName", "senderName", "calendarUrl", "signature", "dailyLimit", "interval"];
  const warmupFields = ["dailyEmailLimit", "emailIncPerDayLimit", "replyRate"];
  const init = {};
  // Only prefill a field when exactly one inbox is selected. For multi-select,
  // always start blank (placeholder) so a specific inbox's value isn't shown as
  // if it applied to all — the user types only what they want to bulk-apply.
  const isSingle = inboxes.length === 1;
  topFields.forEach(f => {
    init[f] = isSingle ? String(inboxes[0][f] ?? "") : MIXED;
  });
  warmupFields.forEach(f => {
    init[f] = isSingle
      ? String(inboxes[0]?.emailWarming?.warmupSettings?.[f] ?? "")
      : MIXED;
  });
  return init;
};

const BulkEditInboxFieldsPopup = observer(() => {
  const selectedIds = connectedInboxStore.selectedInboxIds;
  const allInboxes = connectedInboxStore.accountData;
  const selectedInboxes = allInboxes.filter(i => selectedIds.includes(i.id));
  const count = selectedInboxes.length;

  const [form, setForm] = useState(() => resolveInitial(selectedInboxes));
  const [saving, setSaving] = useState(false);

  // Stable initial signature for the editor's prefilledValue. Captured once on
  // mount (like Configure passes the original `signature`, not live state) so the
  // editor isn't re-seeded on every keystroke. Empty when values are mixed.
  const [initialSignature] = useState(() =>
    form.signature === MIXED ? "" : form.signature ?? ""
  );

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // Only persist edits the user actually makes; ignore the editor's mount/api
  // emit so untouched signatures aren't overwritten across selected inboxes.
  const handleSignatureChange = (value, source) => {
    if (source === "user") setField("signature", value);
  };

  // Warmup status analysis
  const warmupEnabledInboxes = selectedInboxes.filter(i => i.warmingEnabled);
  const warmupDisabledCount = count - warmupEnabledInboxes.length;
  const allWarmupDisabled = warmupDisabledCount === count;
  const someWarmupDisabled = warmupDisabledCount > 0 && !allWarmupDisabled;
  const isSingleInbox = count === 1;

  // Per-inbox "Max emails per day" cannot exceed the global daily email limit
  // configured in Email Settings (same rule the single-inbox Configure enforces).
  const globalDailyLimit = userDetail?.userFeatureFlag?.dailySendLimit;
  const dailyLimitError =
    form.dailyLimit !== MIXED &&
    form.dailyLimit !== "" &&
    globalDailyLimit != null &&
    Number(form.dailyLimit) > Number(globalDailyLimit)
      ? `Email daily limit cannot be greater than your global daily email limit of ${globalDailyLimit}`
      : "";

  const warmupBannerMsg = useMemo(() => {
    if (allWarmupDisabled) {
      if (isSingleInbox) {
        return "Warmup is not enabled for this inbox. Enable warmup first to update these settings.";
      }
      return "None of the selected inboxes have warmup enabled. Enable warmup first to update these settings.";
    }
    if (someWarmupDisabled) {
      const enabledCount = warmupEnabledInboxes.length;
      return `Warming settings will apply only to ${enabledCount} inbox${enabledCount !== 1 ? "es" : ""} that have warmup enabled.`;
    }
    return null;
  }, [allWarmupDisabled, someWarmupDisabled, warmupEnabledInboxes.length, isSingleInbox]);

  const handleSave = async () => {
    if (dailyLimitError) return;
    const numFields = new Set(["dailyLimit", "interval", "dailyEmailLimit", "emailIncPerDayLimit", "replyRate"]);
    const warmupFields = new Set(["dailyEmailLimit", "emailIncPerDayLimit", "replyRate"]);
    const payload = {};
    const warmupPayload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== MIXED && v !== "") {
        const val = numFields.has(k) ? Number(v) : v;
        if (warmupFields.has(k)) {
          warmupPayload[k] = val;
        } else {
          payload[k] = val;
        }
      }
    });
    if (!allWarmupDisabled && Object.keys(warmupPayload).length > 0) {
      payload.warmupSettings = warmupPayload;
    }
    setSaving(true);
    await connectedInboxStore.bulkUpdateInboxFields(payload);
    setSaving(false);
  };

  const CHIP_SHOW = 5;
  const extraChips = selectedInboxes.length - CHIP_SHOW;

  const getInboxEmail = inbox => {
    return inbox.type === "smtp" ? inbox?.smtp?.email || inbox.email : inbox.email;
  };

  return (
    <div
      className="bulkEditInboxOverlay"
      onClick={() => connectedInboxStore.setBulkEditPopupOpen(false)}
    >
      <div
        className="bulkEditInboxModal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bulkEditInboxHeader">
          <div className="bulkEditInboxTitleRow">
            <div>
              <h3 className="bulkEditInboxTitle">Edit Fields</h3>
              <p className="bulkEditInboxSubtitle">
                Editing{" "}
                <span className="bulkEditInboxCount">{count} inbox{count !== 1 ? "es" : ""}</span>
                . Only filled fields will be updated.
              </p>
            </div>
            <button
              className="bulkEditCloseBtn"
              onClick={() => connectedInboxStore.setBulkEditPopupOpen(false)}
            >
              <i className="material-icons">close</i>
            </button>
          </div>
          <div className="bulkEditInboxChips">
            {selectedInboxes.slice(0, CHIP_SHOW).map(inbox => (
              <div key={inbox.id} className="inboxChip">
                <InboxImage type={inbox.type} />
                <span className="inboxChipEmail">{getInboxEmail(inbox)}</span>
              </div>
            ))}
            {extraChips > 0 && (
              <div
                className="inboxChipMore"
                title={selectedInboxes.slice(CHIP_SHOW).map(getInboxEmail).join(", ")}
              >
                +{extraChips} more
              </div>
            )}
          </div>
        </div>

        {/* Form body */}
        <div className="bulkEditInboxBody">
          {/* From Name & Sender Name */}
          <div className="bulkEditFieldRow twoCol">
            <div className="bulkEditField">
              <label className="bulkEditFieldLabel">From Name</label>
              <input
                className="bulkEditInput"
                value={form.fromName === MIXED ? "" : form.fromName ?? ""}
                placeholder="e.g. Arjun from Acme"
                onChange={e => setField("fromName", e.target.value)}
              />
            </div>
            <div className="bulkEditField">
              <label className="bulkEditFieldLabel">Sender Name</label>
              <input
                className="bulkEditInput"
                value={form.senderName === MIXED ? "" : form.senderName ?? ""}
                placeholder="e.g. Arjun Mehta"
                onChange={e => setField("senderName", e.target.value)}
              />
            </div>
          </div>

          {/* Calendar URL */}
          <div className="bulkEditField">
            <label className="bulkEditFieldLabel">Calendar URL</label>
            <input
              className="bulkEditInput"
              value={form.calendarUrl === MIXED ? "" : form.calendarUrl ?? ""}
              placeholder="e.g. https://calendly.com/arjun"
              onChange={e => setField("calendarUrl", e.target.value)}
            />
          </div>

          {/* Email Daily Limit */}
          <div className="bulkEditFieldGroup">
            <label className="bulkEditFieldLabel">Email Daily Limit</label>
            <div className="bulkEditFieldRow twoCol">
              <div className="bulkEditField">
                <div className="bulkEditSubLabel">Max emails per day</div>
                <input
                  className={`bulkEditInput${dailyLimitError ? " hasError" : ""}`}
                  type="number"
                  value={form.dailyLimit === MIXED ? "" : form.dailyLimit ?? ""}
                  placeholder="e.g. 150"
                  onChange={e => setField("dailyLimit", e.target.value)}
                />
                {dailyLimitError && (
                  <div className="bulkEditFieldError">{dailyLimitError}</div>
                )}
              </div>
              <div className="bulkEditField">
                <div className="bulkEditSubLabel">Min delay between emails (seconds)</div>
                <input
                  className="bulkEditInput"
                  type="number"
                  value={form.interval === MIXED ? "" : form.interval ?? ""}
                  placeholder="e.g. 120"
                  onChange={e => setField("interval", e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Signature */}
          <div className="bulkEditField bulkEditSignatureField">
            <label className="bulkEditFieldLabel">Signature</label>
            <div className="bulkEditSignatureEditor">
              <QuilEditor
                bodyOnChange={handleSignatureChange}
                prefilledValue={initialSignature}
                inboxIds={selectedIds}
              />
            </div>
          </div>

          {/* Warmup Settings section */}
          <div className={`bulkEditWarmupSection${allWarmupDisabled ? " warmupDisabled" : ""}`}>
            <div className="bulkEditSectionDivider">
              <span className="bulkEditSectionLabel">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="#f59e0b">
                  <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z" />
                </svg>
                Warmup Settings
              </span>
            </div>

            {warmupBannerMsg && (
              <div className={`warmupStatusBanner${allWarmupDisabled ? " bannerError" : " bannerWarning"}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                <span>{warmupBannerMsg}</span>
              </div>
            )}

            <div className={`bulkEditFieldRow threeCol${allWarmupDisabled ? " fieldGroupDisabled" : ""}`}>
              <div className="bulkEditField">
                <label className="bulkEditFieldLabel">Daily Warmup Limit</label>
                <div className="bulkEditSubLabel">Recommended: 20–40</div>
                <input
                  className="bulkEditInput"
                  type="number"
                  disabled={allWarmupDisabled}
                  value={form.dailyEmailLimit === MIXED ? "" : form.dailyEmailLimit ?? ""}
                  placeholder="e.g. 30"
                  onChange={e => setField("dailyEmailLimit", e.target.value)}
                />
              </div>
              <div className="bulkEditField">
                <label className="bulkEditFieldLabel">Daily Increase</label>
                <div className="bulkEditSubLabel">Recommended: 1–4</div>
                <input
                  className="bulkEditInput"
                  type="number"
                  disabled={allWarmupDisabled}
                  value={form.emailIncPerDayLimit === MIXED ? "" : form.emailIncPerDayLimit ?? ""}
                  placeholder="e.g. 2"
                  onChange={e => setField("emailIncPerDayLimit", e.target.value)}
                />
              </div>
              <div className="bulkEditField">
                <label className="bulkEditFieldLabel">Reply Rate</label>
                <div className="bulkEditSubLabel">Recommended: 30%</div>
                <div className="bulkEditInputWithSuffix">
                  <input
                    className="bulkEditInput"
                    type="number"
                    disabled={allWarmupDisabled}
                    value={form.replyRate === MIXED ? "" : form.replyRate ?? ""}
                    placeholder="e.g. 30"
                    onChange={e => setField("replyRate", e.target.value)}
                  />
                  <span className="bulkEditInputSuffix">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bulkEditInboxFooter">
          <span className="bulkEditFooterHint">Fields left blank won't be changed.</span>
          <div className="bulkEditFooterActions">
            <button
              className="bulkEditCancelBtn"
              onClick={() => connectedInboxStore.setBulkEditPopupOpen(false)}
            >
              Cancel
            </button>
            <button
              className={`bulkEditSaveBtn${saving ? " loading" : ""}`}
              onClick={handleSave}
              disabled={saving || !!dailyLimitError}
            >
              {saving ? "Saving..." : `Save to ${count} inbox${count !== 1 ? "es" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default BulkEditInboxFieldsPopup;
